import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend
} from "recharts";
import { ChartLineUp, Users, MapPin, Trophy, Disc, Clock, ShieldStar } from "@phosphor-icons/react";

export default function AdminAnalytics() {
  const [a, setA] = useState(null);
  const [range, setRange] = useState(30);

  useEffect(() => {
    api.get(`/admin/analytics?days=${range}`).then(({ data }) => setA(data));
  }, [range]);

  if (!a) return <div className="p-10 text-ink-dim">LOADING ANALYTICS…</div>;

  const maxGeo = Math.max(...a.geo.map((g) => g.users));
  const genreData = a.genre_distribution.length
    ? a.genre_distribution.map((g) => ({ genre: g.genre.toUpperCase(), value: g.count }))
    : [{ genre: "TRAP", value: 1 }, { genre: "RAGE", value: 0 }, { genre: "DRILL", value: 0 }];

  return (
    <div className="p-6 lg:p-8" data-testid="admin-analytics">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-2">◢ ANALYTICS</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
            USER <span className="text-brand-red neon-red">SIGNAL.</span>
          </h1>
        </div>
        <div className="flex gap-1 border border-zinc-800" data-testid="range-toggle">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              data-testid={`range-${d}`}
              className={`px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase transition-colors ${
                range === d ? "bg-brand-red text-black" : "text-ink-mute hover:text-white"
              }`}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <QuickStat label="NEW USERS · 7D" value={a.new_users_7d} Icon={Users} color="text-brand-red" testid="qs-new" />
        <QuickStat label="RETENTION · 7D" value={`${a.retention_pct}%`} Icon={ShieldStar} color="text-brand-green" testid="qs-retention" />
        <QuickStat label="TOP PRESETS" value={a.top_presets.length} Icon={Disc} color="text-brand-purple" testid="qs-presets" />
        <QuickStat label="GEO CITIES" value={a.geo.length} Icon={MapPin} color="text-brand-cyan" testid="qs-geo" />
      </div>

      {/* DAU + usage minutes */}
      <Panel title="DAILY ACTIVE USERS · USAGE MINUTES" tag="LIVE" testid="dau-chart" className="mb-8">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={a.daily} margin={{ left: -10, right: 20, top: 12, bottom: 0 }}>
            <CartesianGrid stroke="#1a1a1a" vertical={false} />
            <XAxis dataKey="date" stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }} labelStyle={{ color: "#71717A" }} />
            <Line type="monotone" dataKey="dau" name="DAU" stroke="#FF003C" strokeWidth={2} dot={{ r: 2, fill: "#FF003C" }} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="usage_minutes" name="USAGE (min)" stroke="#00FF66" strokeWidth={2} dot={{ r: 2, fill: "#00FF66" }} />
            <Line type="monotone" dataKey="users" name="NEW USERS" stroke="#8B00FF" strokeWidth={2} dot={{ r: 2, fill: "#8B00FF" }} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-5 text-[10px] tracking-[0.3em] uppercase mt-2 flex-wrap">
          <Legend2 color="#FF003C" label="DAU" />
          <Legend2 color="#00FF66" label="USAGE MINUTES" />
          <Legend2 color="#8B00FF" label="NEW USERS" />
        </div>
      </Panel>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Top Presets */}
        <Panel title="MOST DOWNLOADED PRESETS" tag={`${a.top_presets.length}`} testid="top-presets" className="lg:col-span-2">
          {a.top_presets.length === 0 ? (
            <div className="text-xs text-ink-dim py-8 text-center">No preset data yet. Generate some presets to see analytics.</div>
          ) : (
            <ul className="space-y-2">
              {a.top_presets.slice(0, 6).map((p, i) => {
                const max = a.top_presets[0].downloads || 1;
                const pct = (p.downloads / max) * 100;
                return (
                  <li key={p.name} className="flex items-center gap-3" data-testid={`top-preset-${i}`}>
                    <span className="w-6 text-[10px] tracking-[0.3em] uppercase text-ink-dim">#{i + 1}</span>
                    <div className="w-1.5 h-7" style={{ background: p.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold tracking-tight text-sm truncate">{p.name}</div>
                      <div className="h-1 bg-zinc-900 mt-1.5 max-w-[260px]">
                        <div className="h-full bg-gradient-to-r from-brand-red to-brand-purple" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-white font-mono">{p.downloads.toLocaleString()}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {/* Genre Radar */}
        <Panel title="GENRE DISTRIBUTION" tag="LIVE" testid="genre-radar">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={genreData}>
              <PolarGrid stroke="#27272A" />
              <PolarAngleAxis dataKey="genre" tick={{ fill: "#71717A", fontSize: 9, fontFamily: "JetBrains Mono" }} />
              <PolarRadiusAxis tick={{ fill: "#52525b", fontSize: 8 }} stroke="#27272A" />
              <Radar dataKey="value" stroke="#FF003C" fill="#FF003C" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Top Producers */}
        <Panel title="TOP PRODUCERS" tag="LIVE" testid="top-producers" className="lg:col-span-2">
          {a.top_producers.length === 0 ? (
            <div className="text-xs text-ink-dim py-8 text-center">Awaiting producer activity…</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
              {a.top_producers.map((p, i) => (
                <div key={i} className="bg-bg-surface p-4 flex items-center gap-3" data-testid={`top-producer-${i}`}>
                  <div className="w-10 h-10 border border-brand-red bg-brand-red/10 flex items-center justify-center font-display text-sm">
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight text-sm truncate flex items-center gap-2">
                      {p.producer_name}
                      <Trophy size={11} weight="fill" className={i < 3 ? "text-brand-orange" : "text-zinc-700"} />
                    </div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-ink-dim flex items-center gap-3 mt-1">
                      <span>{p.presets} presets</span>
                      <span className="text-brand-red">SCORE {p.score}</span>
                    </div>
                  </div>
                  <span className={`text-[9px] tracking-[0.3em] uppercase px-1.5 py-0.5 border ${
                    p.tier === "pro" ? "border-brand-purple text-brand-purple"
                    : p.tier === "intermediate" ? "border-brand-red text-brand-red"
                    : "border-zinc-700 text-ink-mute"
                  }`}>
                    {p.tier}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Plugin Usage Hours */}
        <Panel title="PLUGIN USAGE HOURS" tag="ESTIMATED" testid="usage-hours">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.daily.slice(-10)} margin={{ left: -20, right: 8, top: 12, bottom: 0 }}>
              <CartesianGrid stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="date" stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(8)} />
              <YAxis stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }} />
              <Bar dataKey="usage_minutes" fill="#8B00FF" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Geo Heatmap */}
      <Panel title="GEOGRAPHIC AUDIENCE HEATMAP" tag={`${a.geo.length} CITIES`} testid="geo-heatmap">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900">
          {a.geo.map((g, i) => {
            const intensity = (g.users / maxGeo) * 100;
            return (
              <div key={i} className="bg-bg-surface p-4 relative overflow-hidden" data-testid={`geo-city-${i}`}>
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(circle at 80% 20%, rgba(255,0,60,${intensity / 200}), transparent 60%)`,
                  }}
                />
                <div className="relative">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-ink-dim">
                    <MapPin size={11} weight="fill" className="text-brand-red" /> {g.country}
                  </div>
                  <div className="font-display font-black text-2xl tracking-tighter mt-2">{g.city}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-ink-dim">USERS</span>
                    <span className="font-display text-lg text-brand-red">{g.users.toLocaleString()}</span>
                  </div>
                  <div className="h-1 bg-zinc-900 mt-3">
                    <div className="h-full bg-gradient-to-r from-brand-red to-brand-purple" style={{ width: `${intensity}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function QuickStat({ label, value, Icon, color, testid }) {
  return (
    <div className="bg-bg-surface p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon size={14} weight="bold" className={color} />
        <Clock size={10} weight="bold" className="text-ink-dim" />
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mt-1">{label}</div>
    </div>
  );
}

function Panel({ title, tag, children, className = "", testid }) {
  return (
    <div className={`border border-zinc-800 bg-bg-surface grain ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
        <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim flex items-center gap-2">
          <ChartLineUp size={11} weight="bold" className="text-brand-red" /> {title}
        </span>
        {tag && <span className="text-[9px] tracking-[0.3em] uppercase text-brand-red animate-flicker">● {tag}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Legend2({ color, label }) {
  return (
    <span className="flex items-center gap-2 text-ink-mute">
      <span className="w-2 h-2" style={{ background: color, boxShadow: `0 0 10px ${color}` }} /> {label}
    </span>
  );
}
